/**
 * Utility functions for chain authentication
 */
/**
 * Create a deterministic, canonical JSON string from an object
 * This ensures consistent hashing across different environments
 */
export function canonicalJsonString(obj) {
    if (typeof obj !== 'object' || obj === null) {
        return JSON.stringify(obj);
    }
    return JSON.stringify(obj, Object.keys(obj).sort());
}
/**
 * Hash a string using SHA-256
 * Returns hex string without 0x prefix
 */
export async function hashString(str) {
    const encoder = new TextEncoder();
    const data = encoder.encode(str);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}
/**
 * Hash a JSON payload deterministically
 */
export async function hashPayload(payload) {
    const canonical = canonicalJsonString(payload);
    return hashString(canonical);
}
/**
 * Create the message to be signed
 * This creates a human-readable message that includes all verification data
 */
export async function createSignatureMessage(options) {
    const { payload, timestamp, domain } = options;
    const payloadHash = await hashPayload(payload);
    let message = `Harmony Request\n`;
    if (domain) {
        message += `Domain: ${domain}\n`;
    }
    message += `Payload: 0x${payloadHash}\n`;
    message += `Timestamp: ${timestamp}`;
    return message;
}
/**
 * Parse a signature message back into its components
 */
export function parseSignatureMessage(message) {
    const lines = message.split('\n');
    const result = {};
    for (const line of lines) {
        if (line.startsWith('Domain: ')) {
            result.domain = line.substring(8);
        }
        else if (line.startsWith('Payload: ')) {
            result.payloadHash = line.substring(9).replace('0x', '');
        }
        else if (line.startsWith('Timestamp: ')) {
            result.timestamp = parseInt(line.substring(11), 10);
        }
    }
    return {
        domain: result.domain,
        payloadHash: result.payloadHash ?? '',
        timestamp: result.timestamp ?? 0,
    };
}
/**
 * Check if a timestamp is expired based on max age
 */
export function isTimestampExpired(timestamp, maxAgeMs = 5 * 60 * 1000) {
    const now = Date.now();
    return now - timestamp > maxAgeMs;
}
/**
 * Hash file data using SHA-256
 * Returns hex string without 0x prefix
 *
 * Works in both browser (File/ArrayBuffer) and worker (ReadableStream) environments
 */
export async function hashFile(data) {
    let buffer;
    if (data instanceof File) {
        // Browser File API
        buffer = await data.arrayBuffer();
    }
    else if (data instanceof ReadableStream) {
        // Worker/Node streams
        const reader = data.getReader();
        const chunks = [];
        let totalLength = 0;
        let result = await reader.read();
        while (!result.done) {
            chunks.push(result.value);
            totalLength += result.value.length;
            result = await reader.read();
        }
        // Combine all chunks into a single buffer
        const combined = new Uint8Array(totalLength);
        let offset = 0;
        for (const chunk of chunks) {
            combined.set(chunk, offset);
            offset += chunk.length;
        }
        buffer = combined.buffer;
    }
    else {
        // ArrayBuffer
        buffer = data;
    }
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}
//# sourceMappingURL=utils.js.map